import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { FaBolt, FaWater, FaCog, FaExternalLinkAlt, FaYoutube, FaLinkedin, FaFacebook } from "react-icons/fa";
import { Link } from "react-router-dom";

const FooterSection = ({ title, children }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="relative group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center py-1.5 cursor-pointer">
        <div className="h-2.5 w-0.5 bg-primary mr-2 group-hover:h-3.5 transition-all duration-300"></div>
        <h3 className="text-sm font-medium text-[#212529]/80 group-hover:text-[#212529] transition-colors duration-300">
          {title}
        </h3>
      </div>

      <div
        className={`absolute left-0 right-0 mt-1 bg-white/40 backdrop-blur-sm rounded-lg shadow-sm border border-white/40 overflow-hidden transition-all duration-300 z-20 ${isHovered
          ? 'max-h-60 opacity-100 transform translate-y-0 min-w-[180px] sm:min-w-[220px]'
          : 'max-h-0 opacity-0 pointer-events-none transform -translate-y-1'
          }`}
      >
        <div className="p-3">
          {children}
        </div>
      </div>
    </div>
  );
};

const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer className="relative z-40 overflow-hidden">
      {/* Glass effect background - matching navbar but more opaque */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#e9ecef]/95 to-[#dee2e6]/95 backdrop-blur-sm border-t border-white/40 shadow-sm -z-10"></div>

      {/* Light effect */}
      <div className="absolute -top-24 -left-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main footer section - Mobile and Desktop optimized */}
        <div className="py-6 space-y-6 md:py-4 md:space-y-3">

          {/* Desktop layout: Single row with company, social, navigation */}
          <div className="hidden md:flex md:items-center md:justify-between md:gap-8">
            {/* Company logo and description */}
            <div className="flex-shrink-0">
              <h2 className="text-xl font-bold text-[#212529] tracking-tight">
                <span className="text-primary">AXI</span>ALIS
              </h2>
              <p className="text-[#212529]/70 text-xs mt-1 max-w-xs font-light">
                Pioneering the next generation of yokeless axial flux electric motors.
              </p>
            </div>

            {/* Social Media Section - Desktop centered */}
            <div className="flex-grow flex flex-col items-center justify-center">
              <h4
                className="text-lg font-bold mb-2 tracking-wide text-primary/90 transform hover:scale-105 transition-transform duration-300"
                style={{ fontFamily: 'Dancing Script, Brush Script MT, cursive' }}
              >
                Follow us for more!
              </h4>
              <div className="flex justify-center space-x-3">
                <a
                  href="https://www.youtube.com/@Axialis-Electricmotors"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#212529]/70 hover:text-[#FF0000] transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 p-1"
                  aria-label="YouTube"
                >
                  <FaYoutube className="text-xl" />
                </a>
                <a
                  href="https://www.linkedin.com/company/axialis-electric-motors/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#212529]/70 hover:text-[#0A66C2] transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 p-1"
                  aria-label="LinkedIn"
                >
                  <FaLinkedin className="text-xl" />
                </a>
                <a
                  href="https://www.facebook.com/share/19LWc6i5Zy/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#212529]/70 hover:text-[#1877F2] transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 p-1"
                  aria-label="Facebook"
                >
                  <FaFacebook className="text-xl" />
                </a>
              </div>
            </div>

            {/* Navigation sections - Desktop horizontal */}
            <div className="flex-shrink-0 flex gap-8">
              <div>
                <h3 className="text-xs font-medium text-[#212529]/90 mb-2 border-b border-[#212529]/30 pb-1">Products</h3>
                <ul className="space-y-1 text-xs">
                  <li>
                    <Link to="/technology" className="text-[#212529]/70 hover:text-primary transition-colors duration-300 flex items-center">
                      <span className="h-[1px] w-2 bg-[#212529]/30 mr-2"></span>
                      Technology
                    </Link>
                  </li>
                  <li>
                    <Link to="/applications" className="text-[#212529]/70 hover:text-primary transition-colors duration-300 flex items-center">
                      <span className="h-[1px] w-2 bg-[#212529]/30 mr-2"></span>
                      Applications
                    </Link>
                  </li>
                  <li>
                    <Link to="/projects" className="text-[#212529]/70 hover:text-primary transition-colors duration-300 flex items-center">
                      <span className="h-[1px] w-2 bg-[#212529]/30 mr-2"></span>
                      Projects
                    </Link>
                  </li>
                  <li>
                    <Link to="/explore-motor" className="text-[#212529]/70 hover:text-primary transition-colors duration-300 flex items-center">
                      <span className="h-[1px] w-2 bg-[#212529]/30 mr-2"></span>
                      Explore Motor
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xs font-medium text-[#212529]/90 mb-2 border-b border-[#212529]/30 pb-1">Company</h3>
                <ul className="space-y-1 text-xs">
                  <li>
                    <Link to="/contact" className="text-[#212529]/70 hover:text-primary transition-colors duration-300 flex items-center">
                      <span className="h-[1px] w-2 bg-[#212529]/30 mr-2"></span>
                      Contact
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Mobile layout: Stacked sections (unchanged) */}
          <div className="md:hidden space-y-6">
            {/* Company logo and description */}
            <div className="text-center">
              <h2 className="text-2xl font-bold text-[#212529] tracking-tight">
                <span className="text-primary">AXI</span>ALIS
              </h2>
              <p className="text-[#212529]/70 text-sm mt-2 max-w-md mx-auto font-light">
                Pioneering the next generation of yokeless axial flux electric motors.
              </p>
            </div>

            {/* Social Media Section - Mobile */}
            <div className="text-center py-6">
              <h4
                className="text-3xl font-bold mb-6 tracking-wide text-primary/90 transform hover:scale-105 transition-transform duration-300"
                style={{ fontFamily: 'Dancing Script, Brush Script MT, cursive' }}
              >
                Follow us for more!
              </h4>
              <div className="flex justify-center space-x-8">
                <a
                  href="https://www.youtube.com/@Axialis-Electricmotors"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#212529]/70 hover:text-[#FF0000] transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 p-2"
                  aria-label="YouTube"
                >
                  <FaYoutube className="text-3xl" />
                </a>
                <a
                  href="https://www.linkedin.com/company/axialis-electric-motors/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#212529]/70 hover:text-[#0A66C2] transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 p-2"
                  aria-label="LinkedIn"
                >
                  <FaLinkedin className="text-3xl" />
                </a>
                <a
                  href="https://www.facebook.com/share/19LWc6i5Zy/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#212529]/70 hover:text-[#1877F2] transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 p-2"
                  aria-label="Facebook"
                >
                  <FaFacebook className="text-3xl" />
                </a>
              </div>
            </div>

            {/* Navigation sections - Mobile layout */}
            <div className="border-t border-white/20 pt-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-[#212529]/90 mb-3 border-b border-[#212529]/30 pb-1">Products</h3>
                  <ul className="space-y-2 text-xs">
                    <li>
                      <Link to="/technology" className="text-[#212529]/70 hover:text-primary transition-colors duration-300 flex items-center py-1">
                        <span className="h-[1px] w-2 bg-[#212529]/30 mr-2"></span>
                        Technology
                      </Link>
                    </li>
                    <li>
                      <Link to="/applications" className="text-[#212529]/70 hover:text-primary transition-colors duration-300 flex items-center py-1">
                        <span className="h-[1px] w-2 bg-[#212529]/30 mr-2"></span>
                        Applications
                      </Link>
                    </li>
                    <li>
                      <Link to="/projects" className="text-[#212529]/70 hover:text-primary transition-colors duration-300 flex items-center py-1">
                        <span className="h-[1px] w-2 bg-[#212529]/30 mr-2"></span>
                        Projects
                      </Link>
                    </li>
                    <li>
                      <Link to="/explore-motor" className="text-[#212529]/70 hover:text-primary transition-colors duration-300 flex items-center py-1">
                        <span className="h-[1px] w-2 bg-[#212529]/30 mr-2"></span>
                        Explore Motor
                      </Link>
                    </li>
                  </ul>
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-[#212529]/90 mb-3 border-b border-[#212529]/30 pb-1">Company</h3>
                  <ul className="space-y-2 text-xs">
                    <li>
                      <Link to="/contact" className="text-[#212529]/70 hover:text-primary transition-colors duration-300 flex items-center py-1">
                        <span className="h-[1px] w-2 bg-[#212529]/30 mr-2"></span>
                        Contact
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Legal links and copyright section - Mobile optimized */}
        <div className="border-t border-white/20 py-3 md:py-2">
          <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:justify-between md:items-center">
            {/* Legal links */}
            <div className="flex flex-wrap justify-center md:justify-start gap-6 md:gap-4">
              <Link to="/terms" className="text-[#212529]/60 hover:text-primary text-sm md:text-xs transition-colors duration-300 relative group py-2">
                {t('footer.terms')}
                <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-primary group-hover:w-full transition-all duration-300"></span>
              </Link>
              <Link to="/privacy" className="text-[#212529]/60 hover:text-primary text-sm md:text-xs transition-colors duration-300 relative group py-2">
                {t('footer.privacy')}
                <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-primary group-hover:w-full transition-all duration-300"></span>
              </Link>
              <Link to="/cookies" className="text-[#212529]/60 hover:text-primary text-sm md:text-xs transition-colors duration-300 relative group py-2">
                {t('footer.cookies')}
                <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-primary group-hover:w-full transition-all duration-300"></span>
              </Link>
            </div>

            {/* Copyright */}
            <div className="text-center md:text-right">
              <div className="text-[#212529]/60 text-sm md:text-xs">
                <p>© {new Date().getFullYear()} <span className="font-medium">Axialis</span></p>
              </div>
            </div>
          </div>
        </div>

        {/* Attribution section at bottom - Mobile optimized */}
        <div className="border-t border-white/20 pt-3 pb-2 md:pt-2 md:pb-1">
          <div className="flex flex-col space-y-3 md:space-y-0 md:flex-row md:items-center md:justify-center md:gap-4 text-center">
            <p className="text-[#212529]/60 text-sm md:text-xs">
              Built and designed by <a
                href="https://rivieratech.solutions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center"
              >
                RivieraTech.Solutions
                <FaExternalLinkAlt className="ml-1 text-xs md:text-[0.6rem]" />
              </a>
            </p>
            <div className="hidden md:block h-3 w-[1px] bg-[#212529]/20"></div>
            <p className="text-[#212529]/60 text-sm md:text-xs">
              Powered and deployed by <a
                href="https://rivieratech.solutions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center"
              >
                RivieraTech.Solutions
                <FaExternalLinkAlt className="ml-1 text-xs md:text-[0.6rem]" />
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;